import { logger } from "@/utils/logger";

/**
 * Standard error messages for user-facing errors
 */
export const AGENT_ERROR_MESSAGES = {
  API_KEY_MISSING:
    "The design assistant is unavailable. Please try again later.",
  TIMEOUT: "The request took too long. Please try again.",
  NETWORK: "Network error. Please check your connection and try again.",
  PARSE_ERROR:
    "I couldn't complete that response. Please send your message again.",
  VALIDATION_ERROR:
    "I couldn't complete that response. Please send your message again.",
  TOKEN_LIMIT:
    "The conversation got too long. Please start a new conversation or try a shorter message.",
  RATE_LIMIT:
    "The design assistant is busy. Please wait a moment and try again.",
  UNKNOWN: "Something went wrong. Please try again.",
} as const;

/**
 * Determines if an error is retryable (transient network/timeout errors)
 */
export function isRetryableError(error: Error | string): boolean {
  const errorMessage = error instanceof Error ? error.message : String(error);
  return (
    errorMessage.includes("timeout") ||
    errorMessage.includes("network") ||
    errorMessage.includes("ECONNRESET") ||
    errorMessage.includes("ETIMEDOUT")
  );
}

/**
 * Checks if an error is a token limit error
 */
export function isTokenLimitError(error: Error | string): boolean {
  const errorMessage = error instanceof Error ? error.message : String(error);
  return (
    errorMessage.includes("token") ||
    errorMessage.includes("exceeds the maximum") ||
    errorMessage.includes("1048576") ||
    errorMessage.includes("context length") ||
    errorMessage.includes("too many tokens")
  );
}

/**
 * Checks if an error is a rate limit error
 */
export function isRateLimitError(error: Error | string): boolean {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const lowerMessage = errorMessage.toLowerCase();
  return (
    lowerMessage.includes("rate limit") ||
    errorMessage.includes("429") ||
    lowerMessage.includes("too many requests")
  );
}

/**
 * Converts an error to a user-friendly message
 */
export function getUserFriendlyError(error: Error | string): string {
  const errorMessage = error instanceof Error ? error.message : String(error);

  if (
    errorMessage.includes("timeout") ||
    errorMessage.includes("took too long")
  ) {
    return AGENT_ERROR_MESSAGES.TIMEOUT;
  }
  if (
    errorMessage.includes("network") ||
    errorMessage.includes("fetch") ||
    errorMessage.includes("connection") ||
    errorMessage.includes("ECONNRESET") ||
    errorMessage.includes("ETIMEDOUT")
  ) {
    return AGENT_ERROR_MESSAGES.NETWORK;
  }
  if (isTokenLimitError(error)) {
    return AGENT_ERROR_MESSAGES.TOKEN_LIMIT;
  }
  if (errorMessage.includes("Failed to get agent response")) {
    return "I'm having trouble responding right now. Please try again in a moment.";
  }
  if (errorMessage.includes("parse") || errorMessage.includes("JSON")) {
    return AGENT_ERROR_MESSAGES.PARSE_ERROR;
  }
  if (isRateLimitError(error)) {
    return AGENT_ERROR_MESSAGES.RATE_LIMIT;
  }

  return AGENT_ERROR_MESSAGES.UNKNOWN;
}

/**
 * Logs an agent error with consistent formatting
 * For token limit errors, logs additional context to help debug the source
 */
export function logAgentError(
  context: string,
  error: Error | string,
  metadata?: Record<string, unknown>
): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorObj = error instanceof Error ? error : new Error(errorMessage);

  // For token limit errors, log at critical level with additional context
  if (isTokenLimitError(error)) {
    logger.error(
      `TOKEN LIMIT EXCEEDED in ${context}`,
      {
        component: context,
        action: "token_limit_exceeded",
        metadata: {
          error: errorMessage,
          errorType: "token_limit",
          // Include session and user IDs if available for debugging
          sessionId: metadata?.sessionId || "unknown",
          userId: metadata?.userId || "unknown",
          // Include any additional context that might help identify the source
          ...metadata,
        },
      },
      errorObj
    );
  } else {
    logger.error(
      `Agent error in ${context}`,
      {
        component: context,
        metadata: {
          error: errorMessage,
          ...metadata,
        },
      },
      errorObj
    );
  }
}

/**
 * Logs an agent warning with consistent formatting
 */
export function logAgentWarning(
  context: string,
  message: string,
  metadata?: Record<string, unknown>
): void {
  logger.warn(`Agent warning in ${context}`, {
    component: context,
    metadata: {
      message,
      ...metadata,
    },
  });
}
